import { useEffect, useRef, useState } from 'react'

import { navigationItems } from '../lib/navigationItems'

const useHighlightIntersectingNavigationItem = () => {
    const [activeSection, setActiveSection] = useState<string | null>(null)

    const navItemsRef = useRef<{ [key: string]: HTMLElement | null }>({})
    const scrollContainerRef = useRef<HTMLDivElement>(null)

    const setNavItemRef = (element: HTMLElement | null, id: string) => {
        if (element) {
            navItemsRef.current[id] = element
        }
    }

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setActiveSection(entry.target.id)
                    }
                })
            },
            {
                root: null,
                rootMargin: '-20% 0px -70% 0px',
                threshold: [
                    0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0,
                ],
            }
        )

        navigationItems.forEach(({ id }) => {
            const element = document.getElementById(id)
            if (element) observer.observe(element)
        })

        return () => {
            navigationItems.forEach(({ id }) => {
                const element = document.getElementById(id)
                if (element) observer.unobserve(element)
            })
        }
    }, [])

    useEffect(() => {
        if (
            !activeSection ||
            !navItemsRef.current[activeSection] ||
            !scrollContainerRef.current
        )
            return

        const activeItem = navItemsRef.current[activeSection] as HTMLElement
        const container = scrollContainerRef.current
        const containerRect = container.getBoundingClientRect()
        const activeRect = activeItem.getBoundingClientRect()

        if (
            activeRect.right > containerRect.right ||
            activeRect.left < containerRect.left
        ) {
            const scrollLeft =
                activeItem.offsetLeft +
                activeItem.offsetWidth -
                container.offsetWidth +
                20

            container.scrollTo({
                left: scrollLeft,
                behavior: 'smooth',
            })
        }
    }, [activeSection])

    return { activeSection, setNavItemRef, scrollContainerRef }
}

export default useHighlightIntersectingNavigationItem
